<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="maxLength"
    :status="status"
    :addon-before="prefix || undefined"
    class="ant-contract-number-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i class="fas fa-file-contract" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  /** Tiền tố hiển thị, vd: HD/ */
  prefix?: string;
  maxLength?: number;
  /** Tự viết hoa */
  uppercase?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "Nhập số hợp đồng",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  prefix: "",
  maxLength: 50,
  uppercase: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
}>();

const displayValue = ref("");

const normalize = (value: string) => {
  let next = value.replace(/[^\w\-\/]/g, "");
  if (props.uppercase) next = next.toUpperCase();
  return next.slice(0, props.maxLength);
};

watch(
  () => props.modelValue,
  (val) => {
    displayValue.value = normalize(val ?? "");
  },
  { immediate: true }
);

const onInput = (val: string) => {
  const next = normalize(val);
  displayValue.value = next;
  emit("update:modelValue", next);
  emit("change", next);
};

const onBlur = (e: FocusEvent) => emit("blur", e);
const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — Số hợp đồng
  Global: <h-contractNumber v-model="contract" prefix="HD/" />
  Import: import { ContractNumberInput } from '@/components/ant/input'

  Props: modelValue, prefix, maxLength, uppercase...
  Events: update:modelValue, change, blur, pressEnter
  Docs: src/components/ant/input/README.md
-->
